import { useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';

import chargingStationIcon from '~/assets/icons/charging-station.svg';
import gasPumpIcon from '~/assets/icons/gas-pump.svg';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	InputAction,
	InputKey,
	Layouts,
	ModalButtonColor,
	ModalType,
	VehicleEngineType,
	VehicleFuelType,
} from '~/shared';

import cross from '~/assets/icons/cross.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { PanelControls } from '~/components/ui/PanelControls';
import { Text } from '~/components/ui/Text';
import { ButtonBuy } from '../../components/ButtonBuy';
import { FuelSlider } from '../../components/FuelSlider';
import { FuelTab } from '../../components/FuelTab';
import { Panel } from '../../components/Panel';
import { PanelHeader } from '../../components/PanelHeader';
import { PanelTitle } from '../../components/PanelTitle';
import { PriceBlock } from '../../components/PriceBlock';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { setInfo, setSelectedType } from '../../redux/slices/gasStationSlice';
import { resetStore } from '../../redux/wrappers';
import {
	StyledPanelControlsContainer,
	StyledWrapper,
} from './GasStation.styled';

// import { ButtonManageBusiness } from '../../components/ButtonManageBusiness';

const GasStation = () => {
	const vehicleFuel = useObservable(rage.vehicles.getLocalVehicleFuel());
	const fuelTankCapacity = useObservable(
		rage.vehicles.getLocalVehicleFuelTankCapacity(),
	);
	const [fuelSliderValue, setFuelSliderValue] = useState(0);
	const [fuelUnitPrice, setFuelUnitPrice] = useState(0);
	const activeGasStationInfo = useObservable(
		rage.gasStation.getActiveGasStationInfo(),
	);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const info = useLayoutSelector((state) => state.gasStation.info);
	const selectedType = useLayoutSelector(
		(state) => state.gasStation.selectedType,
	);
	const dispatch = useLayoutDispatch();

	useEffectOnce(() => {
		// Перетаскивание
		const handleDragging = (e: any) => {
			e.preventDefault();
		};
		document.addEventListener('dragstart', handleDragging);
		return () => document.removeEventListener('dragstart', handleDragging);
	});

	useEffectOnce(() => {
		return () => {
			dispatch(resetStore());
		};
	});

	useEffectOnce(() => {
		dispatch(setInfo(activeGasStationInfo));
		const preSelectedType =
			Object.keys(activeGasStationInfo.fuelTypes)[0] || null;
		dispatch(setSelectedType(preSelectedType));
	});

	useEffect(() => {
		if (info !== null && selectedType !== null) {
			const unitPrice = info.fuelTypes[selectedType];
			if (typeof unitPrice !== 'undefined') {
				setFuelUnitPrice(unitPrice);
			}
		}
	}, [info, selectedType]);

	useEffect(() => {
		const maxAdd = Math.max(0, Math.floor(fuelTankCapacity - vehicleFuel));
		setFuelSliderValue((prev) => Math.min(prev, maxAdd));
	}, [vehicleFuel, fuelTankCapacity]);

	// TODO
	// const handleButtonManageBusiness = () => {};

	const handleDrainFuel = async () => {
		try {
			await rage.gasStation.drainFuel();
			setFuelSliderValue(0);
		} catch (error) {
			rage.notifications.showException(error);
		}
	};

	const handleShowModal = () => {
		if (
			modalState ||
			info?.engineType !== VehicleEngineType.Petrol ||
			vehicleFuel < 1
		) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				acceptButton: {
					color: ModalButtonColor.Danger,
					text: 'Подтвердить',
				},
				cancelButton: {
					color: ModalButtonColor.Secondary,
					text: 'Отменить',
				},
				text: 'Вы действительно хотите слить топливо?',
				title: 'Подтверждение',
			},
			(result) => {
				if (result.isAccepted) {
					handleDrainFuel().catch((err) =>
						rage.notifications.showException(err),
					);
				}
			},
		);
	};

	const handleButtonClose = async () => {
		if (modalState) {
			return;
		}

		await rage.gasStation
			.closePanel()
			.catch((err) => rage.notifications.showException(err));
	};

	const handleSliderChange = (newValue: number) => {
		setFuelSliderValue(newValue);
	};

	const handleButtonBuy = async () => {
		if (selectedType === null || !fuelSliderValue || modalState) {
			return;
		}

		try {
			await rage.gasStation.buyFuel(selectedType, fuelSliderValue);
		} catch (error) {
			rage.notifications.showException(error);
		}
	};

	const handleSelect = (step: number) => {
		if (!info || !selectedType) {
			return;
		}

		const types = Object.keys(info.fuelTypes) as VehicleFuelType[];

		const currentIndex = types.indexOf(selectedType);
		const nextIndex = (currentIndex + step + types.length) % types.length;
		dispatch(setSelectedType(types[nextIndex]));
	};

	const handleMaxFuel = () => {
		handleSliderChange(Math.floor(fuelTankCapacity - vehicleFuel));
	};

	const controls = [
		{
			text: 'Управление',
			keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
		},
		{
			text:
				info?.engineType === VehicleEngineType.Electric
					? 'Макс. заряда'
					: 'Макс. топлива',
			keys: [InputKey.F],
			onClick: handleMaxFuel,
		},
		{
			text: 'Оплатить топливо',
			keys: [InputKey.Enter],
			onClick: handleButtonBuy,
		},
		...(info?.engineType === VehicleEngineType.Petrol
			? [
					{
						text: 'Слить топливо',
						keys: [InputKey.C],
						onClick: handleShowModal,
					},
			  ]
			: []),
		{
			text: 'Закрыть окно',
			keys: ['Esc'],
			onClick: handleButtonClose,
		},
	];

	useInputAction(InputAction.CommonMenuNavLeft, () => handleSelect(-1));
	useInputAction(InputAction.CommonMenuNavRight, () => handleSelect(1));
	useInputAction(InputAction.GasStationNavLeft, () => handleSelect(-1));
	useInputAction(InputAction.GasStationNavRight, () => handleSelect(1));
	useInputAction(InputAction.CommonMenuAccept, handleButtonBuy);
	useInputAction(InputAction.GasStationDrainFuel, handleShowModal);
	useInputAction(InputAction.GasStationFullTank, handleMaxFuel);
	useInputAction(InputAction.CommonMenuClose, handleButtonClose);

	if (info === null) {
		return null;
	}

	return (
		<StyledWrapper className={Layouts.GasStation}>
			<Panel>
				<PanelHeader>
					<PanelTitle>
						<img
							src={
								info.engineType === VehicleEngineType.Electric
									? chargingStationIcon
									: gasPumpIcon
							}
						/>
						<span>
							{info.engineType === VehicleEngineType.Electric ? 'ЭЗС' : 'АЗС'} #
							{info.id + 1}
						</span>
					</PanelTitle>
					<div>
						{/* <ButtonManageBusiness onClick={handleButtonManageBusiness}>
						Управление бизнесом
					</ButtonManageBusiness> */}
						{info.engineType == VehicleEngineType.Petrol && (
							<Button
								color={ButtonColors.Cancel}
								height={ButtonHeight.Height30}
								style={{ width: '115px', padding: '0' }}
								onClick={handleShowModal}
								disabled={vehicleFuel < 1}
							>
								<Text>Слить топливо</Text>
							</Button>
						)}
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							style={{ width: '30px' }}
							onClick={handleButtonClose}
						>
							<img src={cross} width={14} height={14} />
						</Button>
					</div>
				</PanelHeader>
				<div style={{ marginTop: 20 }}>
					{Object.keys(info.fuelTypes).map((type, index) => (
						<FuelTab key={`tab#${index}`} type={type as VehicleFuelType} />
					))}
				</div>
				<div style={{ marginTop: 30 }}>
					<FuelSlider
						min={vehicleFuel}
						max={fuelTankCapacity}
						fuelSliderValue={fuelSliderValue}
						engineType={info.engineType}
						onChange={handleSliderChange}
					/>
					<PriceBlock
						price={fuelSliderValue * fuelUnitPrice}
						engineType={info.engineType}
					/>
					<ButtonBuy
						onClick={handleButtonBuy}
						engineType={info.engineType}
						disabled={!fuelSliderValue}
					>
						{(() => {
							switch (info.engineType) {
								case VehicleEngineType.Electric:
									return 'Оплатить';
								case VehicleEngineType.Petrol:
									return 'Оплатить топливо';
								default:
									return null;
							}
						})()}
					</ButtonBuy>
				</div>
			</Panel>
			<StyledPanelControlsContainer>
				<PanelControls controls={controls} />
			</StyledPanelControlsContainer>
		</StyledWrapper>
	);
};

export { GasStation };
